import { useCallback, useEffect, useMemo, useState, type ReactNode } from "react";
import {
  BLOQUE_ESQUELETO,
  EsqueletoSeccion,
  claseContenido,
} from "../components/EsqueletoSeccion";
import {
  cargarResumenReferidos,
  compartirReferido,
  linkDeReferido,
  type ResumenReferidos,
} from "../lib/referidos";
import {
  TiraDeFotos,
  LuzAzul,
  NEGRO_REFERIDOS as NEGRO,
} from "../components/referidos/TiraDeFotos";

/**
 * "Invitá y ganá" — la pantalla del programa de referidos
 * (ADR-2026-08-27-programa-referidos, rediseñada en
 * ADR-2026-08-28-referidos-mas-visual).
 *
 * ── La idea ────────────────────────────────────────────────────────────────
 * En Atlas un crédito **es** una foto (`utils/credits.ts`). Así que lo que se
 * regala no se ilustra: se muestra. El encabezado es una tira con las fotos que
 * la propia cuenta generó, sobre oscuro, y el trato escrito encima.
 *
 * Es a propósito lo único oscuro de la app. Un panel azul plano ya lo usa la
 * tarjeta de configuración de la home, y repetirlo acá habría hecho de las dos
 * cosas "otro cartel de la home". Sobre negro las fotos son lo que brilla, que
 * es exactamente el orden de importancia que esta pantalla necesita: primero lo
 * que ganás, después cómo se consigue.
 *
 * ── Por qué cada bloque está donde está ────────────────────────────────────
 * El orden sale de mirar ~25 programas reales en Mobbin (Instacart, Cleo,
 * Monzo, Chime, Acorns, Satispay, Monarch, Opal, Numo, HelloFresh, foodpanda):
 *
 *  1. La OFERTA como título y la acción pegada abajo. Es lo único que se le
 *     pide al usuario; todo lo demás es información sobre eso.
 *  2. Los números grandes y el CAMINO a los premios en un solo bloque. La
 *     escalera va como línea con los hitos ubicados a distancia real (5 cerca,
 *     25 lejos): una lista los dibujaría a los tres igual de cerca, que es
 *     mentira y le saca urgencia al primero.
 *  3. "Cómo funciona" con la condición de pago escrita — lo que evita el
 *     "invité a tres y no me pagaron".
 *  4. Los amigos con su estado, que es lo que contesta "¿y el mío?".
 *
 * Los montos NO están hardcodeados: vienen de `referidos_config` por
 * `referidos_resumen`, porque la especificación los declara a calibrar.
 */

/* OJO con los tokens de marca y la opacidad: `brand-primary`/`brand-black` están
   declarados como `var(--brand-*)` SIN `<alpha-value>`, así que un modificador
   como `text-brand-black/50` o `bg-brand-primary/[0.08]` **no emite ninguna
   regla** — el texto hereda el color del padre y el fondo queda transparente.
   Se comprobó en vivo: `text-brand-black/50` computaba `rgb(22,24,29)`, el mismo
   near-black de los títulos, o sea toda la jerarquía de esta pantalla aplanada.
   Por eso acá los grises van con `text-black/<n>` y los tintes con el hex
   literal del azul de marca. El token pelado (`text-brand-primary`,
   `bg-brand-primary`) sí funciona y se usa tal cual. */

const CLASE_TARJETA =
  "rounded-2xl border border-black/[0.06] bg-white p-4 sm:p-6";

/** Cuánto dura el "Link copiado" antes de volver al estado normal. */
const MS_FEEDBACK = 2000;

/**
 * Las fotos del encabezado. Son un set curado de generaciones de Atlas, servido
 * desde `public/`, y no las de la propia cuenta.
 *
 * Antes se traían las últimas generaciones del usuario. La idea era linda —lo
 * que regalás son fotos, así que mostrás las tuyas— pero tenía dos agujeros que
 * en producción pesan más: una cuenta recién creada, que es JUSTO la que más
 * conviene que invite, veía la tira vacía; y las que sí tenían fotos dependían
 * de URLs firmadas que vencen a la hora y de que existiera la miniatura `opt/`,
 * que para muchas generaciones no existe. Con un set fijo el encabezado se ve
 * igual de bien el primer día que el día cien, sin consultas ni firmas.
 *
 * Las que sobran de 3 se ocultan en mobile: a 390px, seis columnas quedan en
 * ~55px y no se distingue ninguna.
 */

export const Referidos = () => {
  const [resumen, setResumen] = useState<ResumenReferidos | null>(null);
  const [cargando, setCargando] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [aviso, setAviso] = useState<string | null>(null);

  /* Si el navegador tiene hoja de compartir nativa. Se mira una sola vez y en un
     inicializador de estado (no en el render) porque `navigator` no existe
     durante un render de servidor y porque el valor no cambia en toda la vida de
     la pantalla. Decide únicamente la ETIQUETA del botón; la acción es la misma
     `compartirReferido`, que ya cae al portapapeles cuando no hay hoja. */
  const [puedeCompartir] = useState(
    () => typeof navigator !== "undefined" && Boolean(navigator.share),
  );

  useEffect(() => {
    let vivo = true;
    (async () => {
      try {
        const r = await cargarResumenReferidos();
        if (vivo) setResumen(r);
      } catch (e) {
        // `sin_empresa`: el usuario no terminó el onboarding, así que no hay
        // dónde acreditarle nada. Es un estado normal, no una falla.
        const msg = (e as { message?: string })?.message || "";
        if (vivo) {
          setError(
            msg.includes("sin_empresa")
              ? "Terminá de configurar tu cuenta para conseguir tu link de invitación."
              : "No pudimos cargar tus invitaciones. Probá de nuevo en unos segundos.",
          );
        }
      } finally {
        if (vivo) setCargando(false);
      }
    })();
    return () => {
      vivo = false;
    };
  }, []);

  useEffect(() => {
    if (!aviso) return;
    const t = setTimeout(() => setAviso(null), MS_FEEDBACK);
    return () => clearTimeout(t);
  }, [aviso]);

  /* El texto que viaja en el WhatsApp. Lo escribe la app y no el usuario: si la
     hoja de compartir sale vacía, la mayoría manda el link pelado y el amigo no
     entiende por qué debería tocarlo.

     Dice "al activar tu plan" y no "arrancás con": el premio ya no se paga al
     registrarse. Sin esa aclaración el mensaje prometía en nombre del usuario
     algo que Atlas no cumple, y el que queda mal con su amigo es él. */
  const textoInvitacion = useMemo(
    () =>
      resumen
        ? `Estoy usando Estudio Atlas para hacer las fotos de mis productos con IA. Si entrás con mi link te regalan ${resumen.creditos_amigo_nuevo} créditos al activar tu plan:`
        : "",
    [resumen],
  );

  // Con el programa pausado el servidor rechaza el código y no acredita nada, así
  // que compartir el link sería repartir una oferta que el invitado no va a cobrar.
  const pausado = !!resumen && !resumen.activo;

  const alCompartir = useCallback(async () => {
    if (!resumen || !resumen.activo) return;
    const r = await compartirReferido(resumen.codigo, textoInvitacion);
    if (r === "copiado") setAviso("Link copiado");
    else if (r === "error") setAviso("No pudimos compartir el link");
    // 'compartido' y 'cancelado' no avisan: el usuario ya vio la hoja del
    // sistema, y un cartel encima sería ruido sobre algo que pasó a la vista.
  }, [resumen, textoInvitacion]);

  /** Hitos ordenados y con su estado ya resuelto — los usan la línea y el pie. */
  const hitos = useMemo(() => {
    if (!resumen) return [];
    return [...(resumen.hitos_config || [])]
      .sort((a, b) => a.amigos - b.amigos)
      .map((h) => {
        const logrado = resumen.hitos.find((x) => x.amigos === h.amigos);
        return {
          ...h,
          acreditado: logrado?.estado === "acreditado",
          enRevision: logrado?.estado === "pendiente_revision",
        };
      });
  }, [resumen]);

  const proximoHito = useMemo(
    () => hitos.find((h) => !h.acreditado && !h.enRevision) || null,
    [hitos],
  );

  return (
    <>
      {cargando && (
        <EsqueletoSeccion
          className="px-4 sm:px-8 pb-8 max-w-content mx-auto"
          claseFilaTitulo="hidden"
        >
          <div className={`mb-5 h-[500px] rounded-3xl ${BLOQUE_ESQUELETO}`} />
          <div className="mb-5 grid gap-4 lg:grid-cols-[1fr_1.05fr]">
            <div className="rounded-2xl border border-black/[0.06] bg-white p-6">
              <div className={`h-[62px] ${BLOQUE_ESQUELETO}`} />
            </div>
            <div className="rounded-2xl border border-black/[0.06] bg-white p-6">
              <div className={`h-[62px] ${BLOQUE_ESQUELETO}`} />
            </div>
          </div>
          <div className="rounded-2xl border border-black/[0.06] bg-white p-6">
            <div className={`h-[26px] w-[140px] mb-4 ${BLOQUE_ESQUELETO}`} />
            <div className={`h-[110px] ${BLOQUE_ESQUELETO}`} />
          </div>
        </EsqueletoSeccion>
      )}

      <div
        className={`px-4 sm:px-8 pb-8 max-w-content mx-auto ${claseContenido(!cargando)}`}
      >
        {/* Sin título de sección, a pedido: el encabezado oscuro ya dice qué es
            esto con más fuerza que un `<h1>` de 17px, y con el título arriba eran
            dos encabezados seguidos diciendo lo mismo. Ojo: al no haber `<h1>` el
            shell tampoco suma sus 40px de aire (la regla `:has(> h1)` de
            `index.css`), así que el esqueleto de abajo TAMBIÉN va sin fila de
            título — si no, la pantalla salta 40px al terminar de cargar. */}
        {error && (
          <div className={CLASE_TARJETA}>
            <p className="text-[15px] text-black/80">{error}</p>
          </div>
        )}

        {resumen && !resumen.activo && (
          <div className={`${CLASE_TARJETA} mb-5`}>
            <p className="text-[15px] text-black/80">
              El programa de invitaciones está pausado por un rato. Tu código
              sigue siendo el mismo cuando vuelva.
            </p>
          </div>
        )}

        {resumen && (
          <>
            {/* ═══ Encabezado: las fotos, el trato y la acción ═══ */}
            <section
              className="relative mb-5 overflow-hidden rounded-3xl"
              style={{ backgroundColor: NEGRO }}
            >
              <TiraDeFotos />

              {/* La luz azul que baja de las fotos — ver `LuzAzul`. */}
              <LuzAzul className="top-[130px] h-[190px] w-[130%] opacity-[0.28] sm:top-[195px]" />

              <div className="relative z-10 -mt-10 px-5 pb-5 sm:-mt-16 sm:px-8 sm:pb-8">
                {/* Dos títulos según los montos, y no uno solo con variables:
                    cuando los dos lados ganan lo MISMO —hoy, 50 y 50— la gracia
                    de la oferta es justamente que es pareja, y "Regalá 50. Ganá
                    50." repetía el número sin decirlo. Si algún día se vuelven a
                    separar en `referidos_config`, el copy simétrico pasaría a
                    mentir, así que cae solo al que nombra cada monto. */}
                <h2 className="text-[30px] font-bold leading-[1.06] tracking-[-0.02em] text-white sm:text-[42px]">
                  {resumen.creditos_por_amigo === resumen.creditos_amigo_nuevo ? (
                    <>
                      Invitá a un amigo.
                      <br />
                      Ganan {resumen.creditos_por_amigo} créditos los dos.
                    </>
                  ) : (
                    <>
                      Regalá {resumen.creditos_amigo_nuevo} créditos.
                      <br />
                      Ganá {resumen.creditos_por_amigo}.
                    </>
                  )}
                </h2>
                {/* Una sola oración y SIN volver a nombrar los montos: el número
                    ya lo dijo el título, más grande. La bajada no está para
                    repetirlo sino para contestar lo único que el título deja
                    abierto — CUÁNDO cobra cada uno—, y "los" alcanza para
                    referirse a los créditos que el título acaba de nombrar.

                    Sirve igual para las dos formas del título: si los montos se
                    separan en `referidos_config`, "los" sigue apuntando a los
                    créditos del amigo, que son los que el título nombra primero.

                    Antes decía que el amigo cobraba al registrarse y el referidor
                    cuando el amigo empezara a usar Atlas. Las dos mitades
                    quedaron viejas: ahora el premio es uno solo y se paga a los
                    dos en el mismo momento —cuando el amigo activa su plan—, así
                    que la oración además se acortó. Es la línea que evita el
                    "invité a tres y no me pagaron". */}
                <p className="mt-3 max-w-[52ch] text-[14px] leading-snug text-white/60 sm:text-[15px]">
                  {pausado
                    ? "Mientras el programa esté pausado, el link no suma invitaciones."
                    : "Los cobran los dos cuando tu amigo activa su plan."}
                </p>

                {/* Un solo control: el LINK a la vista y la acción adentro.
                    Antes eran dos cajas —el código de 6 letras y un botón
                    aparte—. El código no es lo que se comparte: lo que se pega en
                    un WhatsApp es el link, así que es el link lo que tiene que
                    estar a la vista y listo para copiar. Es además el patrón que
                    usan todos los programas que miramos (Instacart, Setel,
                    QUITTR, TheFork): campo + botón adentro.

                    Se muestra sin `https://` porque esos ocho caracteres no
                    aportan y se comen el ancho del código, que es lo único que
                    distingue un link de otro. Se COPIA entero, igual. */}
                <div className="relative mt-6 sm:max-w-[50%]">
                  <div className="flex items-center gap-1.5 rounded-2xl border border-white/[0.14] bg-white/[0.06] p-1.5 pl-3.5">
                    <span
                      className="min-w-0 flex-1 truncate text-[12.5px] tracking-[-0.01em] text-white/70"
                      title={linkDeReferido(resumen.codigo)}
                    >
                      {linkVisible(resumen.codigo)}
                    </span>
                  <button
                    type="button"
                    onClick={alCompartir}
                    disabled={pausado}
                    title={pausado ? "El programa está pausado" : undefined}
                    className="flex shrink-0 items-center gap-1.5 rounded-xl bg-white px-3.5 py-2.5 text-[13.5px] font-semibold transition-transform active:scale-[0.98] disabled:cursor-not-allowed disabled:opacity-40"
                    style={{ color: NEGRO }}
                  >
                    {/* La etiqueta dice lo que va a pasar de verdad: donde hay
                        hoja de compartir del sistema, comparte; donde no, copia.
                        Un botón que dice "Compartir" y sólo copia al portapapeles
                        deja al usuario esperando un menú que nunca aparece. */}
                    <span className="material-symbols-rounded text-[18px]">
                      {puedeCompartir ? "ios_share" : "content_copy"}
                    </span>
                    {puedeCompartir ? "Compartir" : "Copiar"}
                  </button>
                  </div>

                  {/* El aviso va ABSOLUTO, colgado del campo. Antes ocupaba lugar
                      en el flujo (alto reservado, para que al copiar no saltara
                      el bloque) y esos 26px se sumaban al padding de abajo: el
                      encabezado terminaba con casi el doble de aire abajo que a
                      los costados. Fuera del flujo, el padding queda parejo y el
                      aviso sigue sin mover nada al aparecer. */}
                  <p className="absolute left-0 top-full mt-2 text-[12px] font-semibold text-white/55">
                    {aviso}
                  </p>
                </div>
              </div>
            </section>

            {/* ═══ Cuánto llevás  |  cuánto falta ═══
                Dos tarjetas y no una: son dos preguntas distintas —"¿cuánto
                gané?" mira hacia atrás, "¿cuánto me falta?" hacia adelante— y
                juntas en un bloque la línea de hitos se leía como un pie de los
                números, no como una meta propia.

                La de la derecha va más ancha (`1.15fr`): tiene que dibujar una
                escala de 25 con tres rótulos, mientras que la izquierda son tres
                números. Debajo de `lg` se apilan — a 768 la línea entera en media
                columna deja los rótulos de 5 y 10 encimados. */}
            <div className="mb-5 grid gap-4 lg:grid-cols-[1fr_1.05fr]">
              {/* `justify-between` y no una grilla de tres columnas iguales: con
                  columnas iguales el contenido de cada una se apoyaba a la
                  izquierda y sobraba un hueco grande a la derecha de la tercera.
                  Repartidos, los tres ocupan el ancho de la tarjeta. Y padding
                  lateral propio, más generoso que el de las otras tarjetas,
                  porque acá lo que separa a los ítems entre sí es el aire. */}
              <section
                /* Los paddings laterales van con el MISMO prefijo `sm:` que trae
                    `CLASE_TARJETA` (`sm:p-6`). Sin el prefijo no alcanza: un
                    `pr-11` pelado lo pisa el `sm:p-6` de la clase base —las
                    utilidades con variante se emiten después de las que no la
                    tienen— y el padding derecho quedaba en 24px. Verificado con
                    `getComputedStyle`, no a ojo. En mobile se deja el de la
                    tarjeta: 44px a la derecha en 390px de ancho aprietan las tres
                    cifras. */
                className={`${CLASE_TARJETA} flex flex-col justify-center sm:pl-9 sm:pr-11`}
              >
                <div className="flex items-start justify-between gap-4">
                  <Cifra
                    valor={resumen.creditos_ganados}
                    etiqueta="créditos ganados"
                    icono="auto_awesome"
                    destacada
                  />
                  <Cifra
                    valor={resumen.activados}
                    etiqueta="amigos activaron"
                    icono="group"
                  />
                  <Cifra
                    valor={resumen.pendientes}
                    etiqueta="pendientes"
                    icono="schedule"
                  />
                </div>
              </section>

              <section className={`${CLASE_TARJETA} flex flex-col justify-center`}>
                {/* La meta ARRIBA de la línea: es el título del bloque, no su
                    pie. Abajo se leía como una nota al pie del gráfico; arriba
                    dice qué estás mirando antes de que lo mires. */}
                {proximoHito && (
                  <p className="mb-6 text-[14px] text-black/60">
                    {proximoHito.amigos - resumen.activados <= 0 ? (
                      <>Ya llegaste a {proximoHito.amigos} amigos.</>
                    ) : (
                      <>
                        <span className="font-semibold text-black/85">
                          Te{" "}
                          {proximoHito.amigos - resumen.activados === 1
                            ? "falta 1 amigo"
                            : `faltan ${proximoHito.amigos - resumen.activados} amigos`}
                        </span>{" "}
                        para sumar {proximoHito.creditos} créditos extra, además
                        de los {resumen.creditos_por_amigo} que ganás por cada uno.
                      </>
                    )}
                  </p>
                )}

                {hitos.length > 0 && (
                  <CaminoDeHitos hitos={hitos} activados={resumen.activados} />
                )}
              </section>
            </div>

            {/* ═══ Cómo funciona ═══ */}
            <section className={`${CLASE_TARJETA} mb-5`}>
              <h2 className="mb-5 text-[17px] font-semibold text-black/90">
                Cómo funciona
              </h2>
              <ol className="grid gap-5 sm:grid-cols-3 sm:gap-6">
                <Paso icono="link" titulo="Compartí tu link">
                  Quien entre por él queda asociado a vos, aunque se registre más
                  tarde.
                </Paso>
                <Paso icono="person_add" titulo="Tu amigo se registra">
                  {/* Ya no se compara contra "los 10 del cuestionario": el
                      cuestionario dejó de contar para este programa y nombrarlo
                      acá sugería que registrarse solo ya paga algo. */}
                  Queda asociado a tu link y ve los{" "}
                  {resumen.creditos_amigo_nuevo} créditos que lo esperan.
                </Paso>
                <Paso icono="workspace_premium" titulo="Activa su plan">
                  {/* La condición de pago, explícita. Es la línea que evita el
                      "invité a tres y no me pagaron". */}
                  Recién ahí se acreditan los créditos de los dos. Con el registro
                  solo no alcanza.
                </Paso>
              </ol>
            </section>

            {/* ═══ Tus amigos ═══ */}
            <section className={CLASE_TARJETA}>
              <h2 className="mb-4 text-[17px] font-semibold text-black/90">
                Tus amigos
              </h2>
              {resumen.amigos.length === 0 ? (
                <p className="text-[15px] text-black/55">
                  Todavía no invitaste a nadie. Compartí tu link y los vas a ver
                  acá.
                </p>
              ) : (
                <ul className="divide-y divide-black/[0.06]">
                  {resumen.amigos.map((a, i) => {
                    const activo = a.estado === "activado";
                    return (
                      <li
                        key={`${a.nombre}-${a.fecha}-${i}`}
                        className="flex items-center gap-3 py-3 first:pt-0 last:pb-0"
                      >
                        <span
                          className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-full text-[14px] font-semibold ${
                            activo
                              ? ""
                              : "border border-dashed border-black/15 text-black/35"
                          }`}
                          style={
                            activo
                              ? {
                                  backgroundColor: colorAvatar(a.nombre).fondo,
                                  color: colorAvatar(a.nombre).texto,
                                }
                              : undefined
                          }
                          aria-hidden="true"
                        >
                          {a.nombre.charAt(0).toUpperCase()}
                        </span>
                        <span className="min-w-0 flex-1">
                          <span className="block truncate text-[15px] font-medium text-black/90">
                            {a.nombre}
                          </span>
                          <span className="block text-[13px] text-black/50">
                            {activo
                              ? "Activó su plan"
                              : "Todavía no activó su plan"}
                            {/* La fecha del hecho que nombra el estado, no una
                                sola para las dos filas: en las activadas es
                                cuándo ACTIVÓ y en las pendientes cuándo se
                                registró —que es lo único que pasó todavía—. Con
                                una sola fecha, "Activó su plan · 25/08" podía
                                estar fechando el registro de días antes. */}
                            {(activo ? a.activado : a.fecha) && (
                              <span className="text-black/35">
                                {" · "}
                                {fechaCorta((activo ? a.activado : a.fecha) as string)}
                              </span>
                            )}
                          </span>
                        </span>
                        {activo ? (
                          <span className="shrink-0 text-[15px] font-bold text-brand-primary">
                            {/* Un amigo que activó con el tope del mes lleno vale
                                0 créditos. Se dice acá y no en la letra chica: es
                                el único lugar donde el usuario va a buscar por qué
                                su saldo no subió. */}
                            {a.creditos > 0 ? `+${a.creditos}` : "Tope del mes"}
                          </span>
                        ) : (
                          <span className="shrink-0 rounded-full bg-[#F2F2F7] px-2.5 py-1 text-[12px] font-medium text-[#3C3C43]/70">
                            Pendiente
                          </span>
                        )}
                      </li>
                    );
                  })}
                </ul>
              )}
            </section>

            {/* La letra chica, suelta y no dentro de una tarjeta: son reglas del
                programa, no contenido de ninguno de los bloques de arriba. */}
            <p className="mt-4 px-1 text-[12px] leading-relaxed text-black/40">
              Podés ganar hasta {resumen.tope_mensual} créditos por mes con
              invitaciones (llevás {resumen.creditos_mes} este mes). Contamos una
              sola vez a cada amigo y no valen las cuentas creadas desde tu mismo
              dispositivo. Los premios más grandes pasan por una revisión rápida
              antes de acreditarse.
            </p>
          </>
        )}
      </div>
    </>
  );
};


/**
 * Cómo se MUESTRA el link. Se copia y se comparte siempre el completo
 * (`linkDeReferido`); esto es sólo lo que se lee en el campo.
 *
 * Se le sacan el `https://` y el `&mode=signup`: ninguno de los dos le dice nada
 * al usuario y entre los dos se comen 90px del ancho, que es justo lo que hacía
 * que el código —lo único que distingue un link de otro— quedara cortado por los
 * puntos suspensivos. Es la misma abreviación que hace la barra de cualquier
 * navegador cuando esconde el protocolo.
 */
function linkVisible(codigo: string): string {
  return linkDeReferido(codigo)
    .replace(/^https?:\/\//, "")
    .replace(/[?&]mode=signup/, "");
}

/** Fecha corta, dd/mm/aa. Es la de la lista de amigos y no hace falta más. */
function fechaCorta(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  const dd = String(d.getDate()).padStart(2, "0");
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  return `${dd}/${mm}/${String(d.getFullYear()).slice(-2)}`;
}

/**
 * Colores de los avatares de la lista de amigos.
 *
 * No van del azul de marca: ese azul en esta pantalla significa "crédito ganado"
 * —lo usan las cifras, los hitos cobrados y los "+30" de la propia lista—, y diez
 * círculos azules seguidos lo convertían en una pared que competía justamente con
 * los "+30" que están a la misma altura. Un avatar no es un premio.
 *
 * Tonos suaves con la inicial en la versión oscura del mismo tono: se leen como
 * personas, no como estados. El color sale del nombre, así que a cada amigo le
 * toca siempre el mismo y la lista no cambia de colores entre visitas.
 */
const COLORES_AVATAR = [
  { fondo: "#EDE7F6", texto: "#5E35B1" },
  { fondo: "#E0F2F1", texto: "#00796B" },
  { fondo: "#FFF3E0", texto: "#E65100" },
  { fondo: "#FCE4EC", texto: "#C2185B" },
  { fondo: "#E8F5E9", texto: "#2E7D32" },
];

function colorAvatar(nombre: string) {
  let suma = 0;
  for (let i = 0; i < nombre.length; i++) suma += nombre.charCodeAt(i);
  return COLORES_AVATAR[suma % COLORES_AVATAR.length];
}

/**
 * Un número grande con su ícono al lado y su etiqueta debajo.
 *
 * El ícono va suelto —sin chip ni recuadro— y del mismo alto que el número, así
 * los dos se leen como una sola pieza en vez de un botoncito al lado de una
 * cifra. Toma el COLOR de su número: azul en la cifra destacada, gris en las
 * otras dos. Un ícono gris al lado de un número azul se veía como si le faltara
 * pintarse.
 *
 * El de créditos es `auto_awesome` y no `token_icon.png`: el ícono de token es
 * una imagen y no se puede repintar, así que no podía seguir al número.
 */
function Cifra({
  valor,
  etiqueta,
  icono,
  destacada,
}: {
  valor: number;
  etiqueta: string;
  icono: string;
  destacada?: boolean;
}) {
  return (
    <div>
      <div className="flex items-center gap-1.5 sm:gap-2">
        <span
          className={`material-symbols-rounded shrink-0 text-[24px] leading-none sm:text-[36px] ${
            destacada ? "text-brand-primary" : "text-black/35"
          }`}
          aria-hidden="true"
        >
          {icono}
        </span>
        <p
          /* 26px en mobile contra 40 en desktop: a 390px de ancho las tres
             cifras a 34px ocupaban casi toda la fila y la tarjeta se leía como un
             titular, no como un resumen. */
          className={`text-[26px] font-bold leading-none tracking-[-0.03em] tabular-nums sm:text-[40px] ${
            destacada ? "text-brand-primary" : "text-black/90"
          }`}
        >
          {valor}
        </p>
      </div>
      <p className="mt-2 text-[12px] leading-tight text-black/50 sm:text-[13px]">
        {etiqueta}
      </p>
    </div>
  );
}

/**
 * El camino a los premios: una línea con los hitos ubicados a su distancia
 * REAL (5 está cerca del arranque, 25 al final), la barra llena hasta donde
 * llegó el usuario y un marcador de DÓNDE ESTÁ.
 *
 * Se eligió esto y no la lista de tarjetas que había antes porque una lista
 * dibuja los tres hitos igual de separados, y eso miente: hace parecer que 25
 * amigos están tan cerca como 5 y le saca toda la urgencia al primero, que es
 * justo el que el usuario puede alcanzar.
 *
 * Dos correcciones sobre la primera versión, que se leía como un slider
 * desactivado:
 *  - **Faltaba el "estás acá".** Sin marcador de posición, una línea con tres
 *    candados no dice nada del usuario: dice qué premios existen. El marcador es
 *    lo que la convierte en SU recorrido.
 *  - **Los nodos pesaban demasiado.** Eran tres círculos de 28px con candado,
 *    del mismo tamaño y color entre sí, así que el ojo los leía como el elemento
 *    principal y la barra como decoración. Ahora el hito bloqueado es un punto
 *    chico y sólo el alcanzado crece y se pinta: lo que se destaca es lo que ya
 *    conseguiste, no lo que te falta.
 */
function CaminoDeHitos({
  hitos,
  activados,
}: {
  hitos: { amigos: number; creditos: number; acreditado: boolean; enRevision: boolean }[];
  activados: number;
}) {
  const tope = hitos[hitos.length - 1]?.amigos || 1;
  const pct = (n: number) => Math.min(100, Math.max(0, (n / tope) * 100));
  const posicion = pct(activados);

  /* TODO se centra sobre su nodo, sin casos especiales en los extremos.
     Antes no: la línea iba al ancho completo de la tarjeta y los rótulos de las
     puntas se anclaban al borde (`right: 0` / `left: 0`) para no cortarse. Con
     TRES hitos alcanzaba. Con los cinco de la config real (5/10/15/20/25) dejó
     de alcanzar: anclar el último al borde lo corre media etiqueta hacia
     adentro, y a 320px se comía 8px del rótulo de 20.

     El arreglo es darle aire propio al bloque (el `px-[22px]` de abajo) en vez
     de deformar los extremos. 22px es la mitad del rótulo más ancho, que es
     exactamente lo que necesita el de la punta para centrarse sin salirse; el
     "Estás acá" es más ancho pero sobresale hacia el padding de la tarjeta, que
     no tiene `overflow-hidden`, así que asoma sin cortarse.

     El costo es que la línea ya no va de punta a punta de la tarjeta. Se
     prefiere a la alternativa: una escala que miente sobre dónde cae cada hito.
     Ver ADR-2026-09-01-referidos-se-cobra-al-pagar. */
  const ancla = (p: number) => ({
    left: `${p}%`,
    transform: "translateX(-50%)",
  });

  return (
    /* `px-[22px]`: el aire que necesitan los rótulos de las puntas para
       centrarse sobre su nodo. Va en el CONTENEDOR y no en cada fila para que
       la barra, los nodos, el chip y los rótulos se achiquen todos juntos —si
       sólo se metieran los rótulos, cada uno apuntaría al lugar equivocado. */
    <div className="px-[22px]">
      {/* "Estás acá", arriba de la línea. Va en su propia fila y del lado
          opuesto a los rótulos de los hitos: sobre la línea no hay con qué
          chocar, y así el marcador no compite por el lugar de "+60 / 5 amigos". */}
      {/* El aire se mide contra el NODO, no contra la barra: los círculos
          sobresalen ~9px por encima y por debajo de la línea, así que un `mb-2`
          dejaba al chip prácticamente apoyado sobre el nodo. Con 20px quedan
          ~11px reales de separación, los mismos que abajo. */}
      <div className="relative mb-5 h-[22px]">
        <div className="absolute whitespace-nowrap" style={ancla(posicion)}>
          <span className="rounded-full bg-brand-primary px-2 py-[3px] text-[11px] font-semibold leading-none text-white">
            Estás acá
          </span>
        </div>
      </div>

      <div className="relative h-1.5 rounded-full bg-black/[0.07]">
        <div
          className="absolute inset-y-0 left-0 rounded-full bg-brand-primary transition-[width] duration-700"
          style={{ width: `${posicion}%` }}
          role="progressbar"
          aria-valuenow={activados}
          aria-valuemin={0}
          aria-valuemax={tope}
          aria-label={`Amigos activados: ${activados} de ${tope}`}
        />

        {hitos.map((h) => {
          const alcanzado = h.acreditado || h.enRevision;
          return (
            <div
              key={h.amigos}
              className="absolute top-1/2 -translate-x-1/2 -translate-y-1/2"
              style={{ left: `${pct(h.amigos)}%` }}
            >
              {alcanzado ? (
                <span
                  className={`flex h-6 w-6 items-center justify-center rounded-full ring-[3px] ring-white ${
                    h.acreditado
                      ? "bg-brand-primary text-white"
                      : "bg-amber-400 text-white"
                  }`}
                >
                  <span className="material-symbols-rounded text-[14px]">
                    {h.acreditado ? "check" : "hourglass_top"}
                  </span>
                </span>
              ) : (
                /* Un punto y no un candado: el candado ya lo dice el gris, y
                   tres candados alineados pesaban más que el propio avance. */
                <span className="block h-3 w-3 rounded-full bg-[#C9C9D2] ring-[3px] ring-white" />
              )}
            </div>
          );
        })}

        {/* El marcador del usuario: una raya de 2px que cruza la línea, no un
            círculo. Como círculo tenía el mismo peso y la misma forma que los
            nodos de los hitos, así que a poca distancia de uno —que es donde cae
            casi siempre— se leían como dos hitos pegados en vez de "vos estás
            acá, el premio está allá". Una raya es otra especie de marca: corta la
            línea en lugar de sentarse sobre ella.

            Va ÚLTIMO para quedar por encima cuando cae justo sobre un hito
            recién alcanzado. Alto de 18px contra los 6px de la barra: sobresale
            arriba y abajo, que es lo que la hace visible sobre el tramo azul. */}
        <div
          className="absolute top-1/2 -translate-x-1/2 -translate-y-1/2"
          style={{ left: `${posicion}%` }}
        >
          <span className="block h-[18px] w-[2px] rounded-full bg-brand-primary" />
        </div>
      </div>

      {/* Los rótulos van en su propia fila y no colgando de cada nodo: a 390px
          los de 5 y 10 quedan a 20px uno del otro y se pisan. */}
      <div className="relative mt-5 h-9">
        {hitos.map((h) => (
          <div
            key={h.amigos}
            className="absolute text-center"
            style={ancla(pct(h.amigos))}
          >
            <p
              className={`whitespace-nowrap text-[13px] font-bold leading-none ${
                h.acreditado ? "text-brand-primary" : "text-black/70"
              }`}
            >
              +{h.creditos}
            </p>
            {/* "a los 5" y no "5 amigos": apilado bajo el número, "5 amigos" se
                leía como una tarifa —50 por 5 amigos, o sea 10 cada uno— en vez
                del premio de una vez que es. Va en esta línea y no pegado al
                número porque el "+50" de arriba hace el rótulo tan ancho que los
                de 5 y 10 se pisan.

                Era "al llegar a N" (63px) y con TRES hitos entraba. Con los cinco
                de la config real (5/10/15/20/25) los nodos quedan a 76px y el
                último rótulo va anclado al borde derecho —no centrado, para no
                cortarse—, así que se corre 36px hacia adentro y se comía 19px del
                de 20. La cuenta: el centrado en 80% termina en `303 + w/2` y el
                anclado empieza en `379 - w`, así que hace falta `w < 51px`.
                "a los 25" mide 39 y entra con aire; "25 amigos" mide 54 y NO
                entra. Si algún día se agregan hitos, esto se vuelve a romper: el
                que sume el sexto tiene que rehacer esta cuenta. */}
            <p className="mt-1 whitespace-nowrap text-[11px] leading-none text-black/40">
              {h.enRevision ? "en revisión" : `a los ${h.amigos}`}
            </p>
          </div>
        ))}
      </div>
    </div>
  );
}

/** Un paso de "Cómo funciona": ícono, título y la explicación. */
function Paso({
  icono,
  titulo,
  children,
}: {
  icono: string;
  titulo: string;
  children: ReactNode;
}) {
  return (
    <li>
      <span
        className="mb-3 flex h-10 w-10 items-center justify-center rounded-xl bg-[#1644EB]/[0.08] text-brand-primary"
        aria-hidden="true"
      >
        <span className="material-symbols-rounded text-[20px]">{icono}</span>
      </span>
      <p className="text-[15px] font-semibold text-black/90">{titulo}</p>
      <p className="mt-1 text-[13px] leading-snug text-black/55">
        {children}
      </p>
    </li>
  );
}

export default Referidos;
